import { useState, useEffect, useRef } from "react";
import { Menu, Sun, Moon, ChevronDown, Search, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { NavigationPopup } from "@/components/ui/NavigationPopup";
import { BlogNavigationPopup } from "@/components/Blog/BlogNavigationPopup";
import { motion, AnimatePresence } from "framer-motion";
import { getMainPageUrl, isSubdomain } from "@/lib/subdomain";
import { BrandLogo } from "@/components/BrandLogo";

type BlogNavItem = {
  label: string;
  path?: string;
  id?: string;
  type: "route" | "scroll" | "popup" | "external";
  hasPopup?: boolean;
};

interface BlogHeaderProps {
  showBackButton?: boolean;
  onSearch?: (query: string) => void;
  onMobileMenuOpen?: () => void;
}

// Navigation items - when on blog subdomain, non-blog links go to main site
const getBlogNavItems = (): BlogNavItem[] => {
  const onBlogSubdomain = isSubdomain("blog");

  return [
    {
      label: "Home",
      path: onBlogSubdomain ? getMainPageUrl("/") : "/",
      type: onBlogSubdomain ? "external" : "route",
    },
    {
      label: "Services",
      path: onBlogSubdomain ? getMainPageUrl("/services") : "/services",
      id: "services",
      type: onBlogSubdomain ? "external" : "route",
      hasPopup: true,
    },
    { label: "Blog", path: "/", id: "blog", type: "route", hasPopup: true },
    {
      label: "About",
      path: onBlogSubdomain ? getMainPageUrl("/about") : "/about",
      type: onBlogSubdomain ? "external" : "route",
    },
    {
      label: "Contact",
      id: "contact",
      type: onBlogSubdomain ? "external" : "route",
      path: onBlogSubdomain ? getMainPageUrl("/contact") : "/contact",
    },
  ];
};

export function BlogHeader({
  showBackButton = false,
  onSearch,
  onMobileMenuOpen,
}: BlogHeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDark, setIsDark] = useState<boolean | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Popup state
  const [activePopup, setActivePopup] = useState<string | null>(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const hideTimeout = useRef<number | null>(null);

  // Popup handlers
  const clearHideTimeout = () => {
    if (hideTimeout.current) {
      window.clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  };

  const showPopupFor = (id?: string) => {
    clearHideTimeout();
    if (!id) return;
    // Show popup for services and blog
    if (id === "services" || id === "blog") {
      setActivePopup(id);
      setIsPopupVisible(true);
    }
  };

  const scheduleHidePopup = () => {
    clearHideTimeout();
    hideTimeout.current = window.setTimeout(() => {
      setIsPopupVisible(false);
      setActivePopup(null);
    }, 500);
  };

  const scheduleHidePopupShort = () => {
    clearHideTimeout();
    hideTimeout.current = window.setTimeout(() => {
      setIsPopupVisible(false);
      setActivePopup(null);
    }, 120);
  };

  const scheduleHidePopupImmediate = () => {
    clearHideTimeout();
    setIsPopupVisible(false);
    setActivePopup(null);
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem("theme");
      if (stored === "dark") setIsDark(true);
      else if (stored === "light") setIsDark(false);
      else setIsDark(document.documentElement.classList.contains("white"));
    } catch (e) {
      setIsDark(document.documentElement.classList.contains("white"));
    }
  }, []);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  const toggleTheme = () => {
    try {
      const next = !document.documentElement.classList.contains("dark");
      if (next) {
        document.documentElement.classList.add("dark");
        localStorage.setItem("theme", "dark");
      } else {
        document.documentElement.classList.remove("dark");
        localStorage.setItem("theme", "light");
      }
      setIsDark(next);
    } catch (e) {
      /* ignore */
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch && searchQuery.trim()) {
      onSearch(searchQuery.trim());
    }
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    if (onSearch) {
      onSearch("");
    }
  };

  const handleNavigation = (item: BlogNavItem) => {
    scheduleHidePopupImmediate();
    if (item.type === "external" && item.path) {
      window.location.href = item.path;
    } else if (item.path) {
      navigate(item.path);
    }
  };

  // Get navigation items dynamically based on current subdomain
  const blogNavItems = getBlogNavItems();

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        isScrolled || isPopupVisible
          ? "bg-background border-b border-border"
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-14">
          {/* Logo Section */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (isSubdomain("blog")) {
                  window.location.href = getMainPageUrl("/");
                } else {
                  navigate("/");
                }
              }}
              onMouseEnter={() => scheduleHidePopupShort()}
              className="hover:opacity-80 transition-opacity"
            >
              <BrandLogo priority />
            </button>
            <span className="text-muted-foreground font-normal">/</span>
            <button
              onClick={() => navigate("/")}
              className="text-lg font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              Blog
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-0.5 relative">
            {blogNavItems.map((item) => {
              const hasPopup = item.hasPopup;
              const isActiveRoute =
                item.id === "blog"
                  ? isSubdomain("blog") || location.pathname.startsWith("/blog")
                  : item.path === location.pathname;

              return (
                <button
                  key={item.label}
                  onClick={() => handleNavigation(item)}
                  onMouseEnter={() => {
                    if (hasPopup) {
                      showPopupFor(item.id);
                    } else {
                      scheduleHidePopupShort();
                    }
                  }}
                  onMouseLeave={() => {
                    if (hasPopup) scheduleHidePopup();
                  }}
                  className={`group flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-colors rounded-full ${
                    isActiveRoute
                      ? "bg-slate-100 dark:bg-slate-800 text-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  } ${hasPopup ? "hover:bg-slate-100 dark:hover:bg-slate-800" : ""}`}
                  aria-haspopup={hasPopup ? "true" : undefined}
                  aria-expanded={
                    hasPopup && isPopupVisible && activePopup === item.id
                      ? "true"
                      : "false"
                  }
                >
                  <span className="truncate">{item.label}</span>
                  {hasPopup && (
                    <ChevronDown
                      className={`w-4 h-4 text-muted-foreground transform transition-transform duration-200 ${
                        isPopupVisible && activePopup === item.id
                          ? "rotate-180"
                          : ""
                      } group-hover:rotate-180`}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-2">
            {/* Search */}
            <AnimatePresence>
              {isSearchOpen ? (
                <motion.form
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onSubmit={handleSearchSubmit}
                  className="flex items-center overflow-hidden"
                >
                  <div className="relative">
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search articles..."
                      className="w-48 sm:w-64 h-9 pl-3 pr-8 text-sm bg-muted/50 border border-border rounded-lg outline-none transition-[border-color] focus:border-foreground/40 focus:ring-0"
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </motion.form>
              ) : null}
            </AnimatePresence>

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              aria-label={isSearchOpen ? "Close search" : "Open search"}
            >
              {isSearchOpen ? (
                <X className="h-4 w-4" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>

            {/* Theme toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              aria-label="Toggle theme"
            >
              {isDark ? (
                <Sun className="h-4 w-4" />
              ) : (
                <Moon className="h-4 w-4" />
              )}
            </Button>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden h-9 w-9"
              onClick={() => onMobileMenuOpen?.()}
              aria-label="Open menu"
            >
              <Menu className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Popup for Services and Products */}
      <NavigationPopup
        isVisible={isPopupVisible && activePopup !== "blog"}
        activeItem={activePopup}
        onMouseEnter={() => clearHideTimeout()}
        onMouseLeave={() => scheduleHidePopupImmediate()}
        closePopup={() => scheduleHidePopupImmediate()}
      />

      {/* Blog Navigation Popup */}
      <BlogNavigationPopup
        isVisible={isPopupVisible && activePopup === "blog"}
        activeItem={activePopup}
        onMouseEnter={() => clearHideTimeout()}
        onMouseLeave={() => scheduleHidePopupImmediate()}
        closePopup={() => scheduleHidePopupImmediate()}
      />
    </header>
  );
}
